import { useEffect, useState, useRef, ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ImagePlus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Product,
  useCreateProduct,
  useUpdateProduct,
  useProductCategories,
  useProductOrigins,
} from '@/hooks/warehouse';
import { uploadToCloudinary, getThumbnailImageUrl } from '@/lib/cloudinary';
import FormModal from '@/components/v2/molecules/admin/shared/FormModal';

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  product?: Product | null;
}

const productSchema = z.object({
  name: z.string().min(1, 'El nombre es obligatorio'),
  price: z.coerce.number().min(0, 'El precio debe ser mayor o igual a 0'),
  origin_id: z.string().min(1, 'El origen es obligatorio'),
  category_id: z.string().min(1, 'La categoría es obligatoria'),
  description: z.string().optional(),
  image_url: z.string().nullable().optional(),
});

type ProductFormData = z.infer<typeof productSchema>;

const ProductFormModal = ({ isOpen, onClose, product }: ProductFormModalProps) => {
  // Image states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: createProduct, isPending: isCreating } = useCreateProduct();
  const { mutate: updateProduct, isPending: isUpdating } = useUpdateProduct();
  const { categories } = useProductCategories();
  const { origins } = useProductOrigins();

  const form = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      name: '',
      price: 0,
      origin_id: '',
      category_id: '',
      description: '',
      image_url: null,
    },
  });

  const existingImageUrl = form.watch('image_url');

  useEffect(() => {
    if (isOpen) {
      if (product) {
        form.reset({
          name: product.name,
          price: product.price,
          origin_id: product.origin_id,
          category_id: product.category_id,
          description: product.description || '',
          image_url: product.image_url,
        });
      } else {
        // Set default values from first available options
        const defaultCategoryId = categories[0]?.id || '';
        const defaultOriginId = origins[0]?.id || '';
        
        form.reset({
          name: '',
          price: 0,
          origin_id: defaultOriginId,
          category_id: defaultCategoryId,
          description: '',
          image_url: null,
        });
      }
      setSelectedFile(null);
      setImagePreview(null);
    }
  }, [product, isOpen, form, categories, origins]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    form.setValue('image_url', null, { shouldDirty: true });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (data: ProductFormData) => {
    setIsUploading(true);

    try {
      let imageUrl: string | null | undefined = data.image_url;

      // Upload new image if selected
      if (selectedFile) {
        imageUrl = await uploadToCloudinary(selectedFile);
      }

      const productData = {
        name: data.name.trim(),
        price: data.price,
        origin_id: data.origin_id,
        category_id: data.category_id,
        description: data.description?.trim() || null,
        image_url: imageUrl || null,
      };

      if (product) {
        updateProduct(
          { id: product.id, ...productData },
          { onSuccess: onClose }
        );
      } else {
        createProduct(productData, { onSuccess: onClose });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const currentImage = imagePreview || (existingImageUrl ? getThumbnailImageUrl(existingImageUrl) : null);
  const isLoading = isCreating || isUpdating || isUploading;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={product ? 'Editar Producto' : 'Nuevo Producto'}
      form={form}
      onSubmit={handleSubmit}
      submitLabel={isUploading ? 'Subiendo imagen...' : product ? 'Actualizar' : 'Crear'}
      isLoading={isLoading}
      maxWidth="max-w-[500px]"
    >
      <div className="grid gap-4 py-4">
        {/* Image upload section */}
        <div className="grid gap-2">
          <Label>Imagen del producto (opcional)</Label>
          <div className="flex items-start gap-4">
            <div
              className="relative w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/50 overflow-hidden cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              {currentImage ? (
                <img
                  src={currentImage}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <ImagePlus className="h-8 w-8 text-muted-foreground" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                Seleccionar
              </Button>
              {(currentImage) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveImage}
                  className="text-destructive hover:text-destructive"
                >
                  <X className="h-4 w-4 mr-1" />
                  Eliminar
                </Button>
              )}
            </div>
          </div>
        </div>

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nombre</FormLabel>
              <FormControl>
                <Input placeholder="Nombre del producto" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="price"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Precio de referencia</FormLabel>
              <FormControl>
                <Input type="number" step="0.01" min="0" placeholder="0.00" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="category_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Categoría</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona categoría" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="origin_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Origen</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona origen" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {origins.map((origin) => (
                      <SelectItem key={origin.id} value={origin.id}>
                        {origin.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Descripción (opcional)</FormLabel>
              <FormControl>
                <Textarea placeholder="Descripción del producto..." rows={3} {...field} value={field.value || ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </FormModal>
  );
};

export default ProductFormModal;
